import classNames from 'classnames';
import type { FunctionComponent } from 'react';
import { useCallback } from 'react';

import { DynamicButton } from '@trello/dynamic-tokens';
import { intl } from '@trello/i18n';
import { SubscribeIcon } from '@trello/nachos/icons/subscribe';
import { usePopover } from '@trello/nachos/popover';
import type { BoardHeaderTestIds } from '@trello/test-ids';
import { getTestId } from '@trello/test-ids';

import { useIsBoardHeaderAndMenuRedesignEnabled } from 'app/src/components/BoardHeader/useIsBoardHeaderAndMenuRedesignEnabled';
import { BoardHeaderSubscribedButtonPopover } from './BoardHeaderSubscribedButtonPopover';

import * as styles from './BoardHeaderSubscribedButton.module.less';

interface BoardHeaderSubscribedButtonProps {
  boardId: string;
  isCollapsed?: boolean;
}

export const BoardHeaderSubscribedButton: FunctionComponent<
  BoardHeaderSubscribedButtonProps
> = ({ boardId, isCollapsed }) => {
  const { headerRedesignEnabled } = useIsBoardHeaderAndMenuRedesignEnabled();

  const buttonIcon = (
    <SubscribeIcon
      size={headerRedesignEnabled ? 'medium' : 'small'}
      dangerous_className={styles.subscribedButtonIcon}
    />
  );
  const buttonText = intl.formatMessage({
    id: 'templates.board_header.watch',
    defaultMessage: 'Watching',
    description:
      'The text that appears on the button when the user is watching a board',
  });

  const { triggerRef, toggle, popoverProps } = usePopover<HTMLButtonElement>();

  const onClick = useCallback(() => {
    toggle();
  }, [toggle]);

  return (
    <>
      <DynamicButton
        className={classNames(styles.subscribedButton, {
          [styles.hideText]: isCollapsed,
        })}
        iconBefore={buttonIcon}
        onClick={onClick}
        ref={triggerRef}
        title={buttonText}
        testId={getTestId<BoardHeaderTestIds>('board-watching-icon')}
      >
        {!headerRedesignEnabled && (
          <span className={styles.buttonText}>{buttonText}</span>
        )}
      </DynamicButton>
      <BoardHeaderSubscribedButtonPopover
        boardId={boardId}
        popoverProps={popoverProps}
      />
    </>
  );
};
